import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { EditIcon } from "../../components/Icons/EditIcon";
import { EraseIcon } from "../../components/Icons/EraseIcon";
import { VisibleIcon } from "../../components/Icons/VisibleIcon";
import { NoVisibleIcon } from "../../components/Icons/NoVisibleIcon";

import { ModalLayout } from "../../components/ModalLayout";
import { ImageCropper } from "../../components/ImageCropper";

import { useModal } from "../../hooks/useModal";

import { updateUser } from "../../store/slices";
import { isObjectEmpty } from "../../utils";

import "../../assets/css/profile-modern.css";

const initialValues = {};

export const Profile = () => {
  const user = useSelector((state) => state.user);

  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [edit, setEdit] = useState({});
  const [isVisible, setIsVisible] = useState(false);

  const {
    isOpen,
    handleOpenModal,
    handleCloseModal,
  } = useModal();

  const dispatch = useDispatch();

  const isEditing = Object.values(edit).some(Boolean);

  const handleUpdateImageProfile = (imgSrc) => {
    setValues((prev) => ({
      ...prev,
      avatar: imgSrc,
    }));

    setEdit((prev) => ({
      ...prev,
      avatar: true,
    }));
  };

  const handleRemoveImage = () => {
    setValues((prev) => ({
      ...prev,
      avatar: null,
    }));

    setEdit((prev) => ({
      ...prev,
      avatar: false,
    }));
  };

  const handleChangeValues = (e) => {
    const { name } = e.target;
    let value = e.target.value;

    if (name === "curp") {
      value = value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 18);
    }

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleEdition = (property) => {
    setEdit((prev) => {
      const editing = !prev[property];

      if (editing) {
        setValues((old) => ({
          ...old,
          [property]:
            property === "new_password"
              ? ""
              : user?.[property] ?? "",
        }));
      } else {
        setValues((old) => {
          const next = { ...old };
          delete next[property];
          return next;
        });

        setErrors((old) => {
          const next = { ...old };
          delete next[property];
          return next;
        });
      }

      return {
        ...prev,
        [property]: editing,
      };
    });
  };

  const handleCancelEdition = () => {
    setEdit({});
    setErrors({});
    setValues(initialValues);
    setIsVisible(false);
  };

  const handleSubmit = async () => {
    const errs = {};

    ["name", "lastname", "new_password"].forEach((field) => {
      if (
        edit[field] &&
        (!values[field] || String(values[field]).trim() === "")
      ) {
        errs[field] = "Este campo es requerido.";
      }
    });

    if (
      edit.curp &&
      values.curp &&
      String(values.curp).trim() !== ""
    ) {
      const curpRegex =
        /^[A-Z][AEIOUX][A-Z]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[HM](AS|BC|BS|CC|CL|CM|CS|CH|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TS|TL|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[A-Z0-9]\d$/;

      if (values.curp.length !== 18) {
        errs.curp = "La CURP debe contener exactamente 18 caracteres.";
      } else if (!curpRegex.test(values.curp)) {
        errs.curp = "Ingrese una CURP con formato válido.";
      }
    }

    setErrors(errs);

    if (!isObjectEmpty(errs)) {
      return;
    }

    const userData = new FormData();

    if (edit.avatar && values.avatar) {
      userData.append("avatar", values.avatar);
    }

    [
      "name",
      "lastname",
      "telephone",
      "curp",
      "dob",
      "gender",
      "market_stall",
      "specific_occupation",
      "address",
    ].forEach((field) => {
      if (edit[field]) {
        userData.append(field, values[field] ?? "");
      }
    });

    if (edit.new_password) {
      userData.append("new_password", values.new_password);
    }

    const result = await dispatch(updateUser(userData));

    if (result) {
      setValues({});
      setErrors({});
      setEdit({});
      setIsVisible(false);
    }
  };

  const Field = ({
    property,
    label,
    type = "text",
    placeholder,
    maxLength,
    options,
  }) => {
    const editing = Boolean(edit[property]);

    return (
      <div className={`profile-v2-field ${editing ? "editing" : ""}`}>
        <div className="profile-v2-field-header">
          <label>{label}</label>

          <button
            type="button"
            title={editing ? "Cancelar edición" : `Editar ${label}`}
            onClick={() => handleEdition(property)}
          >
            {editing ? <EraseIcon size={15} /> : <EditIcon size={15} />}
          </button>
        </div>

        {editing ? (
          <>
            {options ? (
              <select
                autoFocus
                name={property}
                value={values[property] ?? ""}
                onChange={handleChangeValues}
              >
                <option value="">Seleccione una opción</option>

                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                autoFocus
                type={type}
                name={property}
                value={values[property] ?? ""}
                placeholder={placeholder}
                maxLength={maxLength}
                autoComplete="off"
                spellCheck={property === "curp" ? false : undefined}
                onChange={handleChangeValues}
              />
            )}

            {errors[property] && <small>{errors[property]}</small>}
          </>
        ) : (
          <div className="profile-v2-value">
            {user?.[property] || "Sin información"}
          </div>
        )}
      </div>
    );
  };

  const avatar =
    user?.avatar ||
    "https://static.vecteezy.com/system/resources/previews/001/840/618/non_2x/picture-profile-icon-male-icon-human-or-people-sign-and-symbol-free-vector.jpg";

  return (
    <section className="profile-v2">
      <div className="profile-v2-page-heading">
        <div>
          <span className="profile-v2-eyebrow">MI CUENTA</span>
          <h1>Mi perfil</h1>
          <p>
            Consulta y actualiza tu información personal, laboral y de acceso.
          </p>
        </div>

        <div className="profile-v2-status">
          <span className="profile-v2-status-dot" />
          Cuenta activa
        </div>
      </div>

      <ModalLayout
        show={isOpen}
        handleCloseModal={handleCloseModal}
        title="Perfil"
      >
        <ImageCropper
          handleUpdateImageProfile={handleUpdateImageProfile}
          handleClose={handleCloseModal}
        />
      </ModalLayout>

      <div className="profile-v2-header">
        <div className="profile-v2-avatar">
          <img
            src={values.avatar ? URL.createObjectURL(values.avatar) : avatar}
            alt="Perfil"
          />

          <button
            type="button"
            className="profile-v2-avatar-edit"
            title="Cambiar foto"
            onClick={handleOpenModal}
          >
            <EditIcon size={15} />
          </button>

          {values.avatar && (
            <button
              type="button"
              className="profile-v2-avatar-remove"
              title="Cancelar cambio de foto"
              onClick={handleRemoveImage}
            >
              ×
            </button>
          )}
        </div>

        <div className="profile-v2-summary">
          <span className="profile-v2-summary-label">PERFIL DEL USUARIO</span>
          <h2>
            {user?.name} {user?.lastname}
          </h2>
          <span className="profile-v2-summary-email">{user?.email}</span>
        </div>

        <div className="profile-v2-header-badge">
          <i className="fas fa-user-check" />
          <div>
            <strong>Información personal</strong>
            <span>Mantén tus datos actualizados</span>
          </div>
        </div>
      </div>

      <div className="profile-v2-section">
        <div className="profile-v2-section-heading">
          <div className="profile-v2-section-icon">
            <i className="far fa-user" />
          </div>

          <div>
            <span>INFORMACIÓN GENERAL</span>
            <h3>Datos personales</h3>
          </div>
        </div>

        <div className="profile-v2-grid">
          <Field property="name" label="Nombres" placeholder="Ingrese sus nombres" />
          <Field property="lastname" label="Apellidos" placeholder="Ingrese sus apellidos" />

          <div className="profile-v2-field profile-v2-readonly">
            <div className="profile-v2-field-header">
              <label>Correo electrónico</label>
            </div>

            <div className="profile-v2-value">
              {user?.email || "Sin información"}
            </div>

            <span className="profile-v2-readonly-note">
              Este dato no puede modificarse desde el perfil.
            </span>
          </div>

          <Field
            property="telephone"
            label="Teléfono"
            type="tel"
            placeholder="Ingrese su teléfono"
          />

          <Field
            property="curp"
            label="CURP"
            placeholder="Ej. RETA900101HQTXXX09"
            maxLength={18}
          />

          <Field property="dob" label="Fecha de nacimiento" type="date" />

          <Field
            property="gender"
            label="Género"
            options={[
              { value: "Masculino", label: "Masculino" },
              { value: "Femenino", label: "Femenino" },
              { value: "Otro", label: "Otro" },
              {
                value: "Prefiero no especificar",
                label: "Prefiero no especificar",
              },
            ]}
          />

          <Field
            property="address"
            label="Dirección"
            placeholder="Ingrese su dirección"
          />
        </div>
      </div>

      <div className="profile-v2-section">
        <div className="profile-v2-section-heading">
          <div className="profile-v2-section-icon">
            <i className="fas fa-briefcase" />
          </div>

          <div>
            <span>CAPACITACIÓN</span>
            <h3>Información laboral</h3>
          </div>
        </div>

        <div className="profile-v2-grid">
          <Field
            property="market_stall"
            label="Puesto"
            placeholder="Ingrese su puesto"
          />

          <Field
            property="specific_occupation"
            label="Ocupación específica"
            placeholder="Ingrese su ocupación"
          />
        </div>
      </div>

      <div className="profile-v2-section">
        <div className="profile-v2-section-heading">
          <div className="profile-v2-section-icon">
            <i className="fas fa-shield-alt" />
          </div>

          <div>
            <span>ACCESO A LA CUENTA</span>
            <h3>Seguridad</h3>
          </div>
        </div>

        <div
          className={`profile-v2-field profile-v2-password ${
            edit.new_password ? "editing" : ""
          }`}
        >
          <div className="profile-v2-field-header">
            <label>Contraseña</label>

            <button
              type="button"
              title={edit.new_password ? "Cancelar cambio" : "Cambiar contraseña"}
              onClick={() => handleEdition("new_password")}
            >
              {edit.new_password ? <EraseIcon size={15} /> : <EditIcon size={15} />}
            </button>
          </div>

          {edit.new_password ? (
            <>
              <div className="profile-v2-password-input">
                <input
                  autoFocus
                  name="new_password"
                  value={values.new_password ?? ""}
                  type={isVisible ? "text" : "password"}
                  placeholder="Nueva contraseña"
                  autoComplete="new-password"
                  onChange={handleChangeValues}
                />

                <button
                  type="button"
                  title={isVisible ? "Ocultar contraseña" : "Mostrar contraseña"}
                  onClick={() => setIsVisible((old) => !old)}
                >
                  {isVisible ? <NoVisibleIcon /> : <VisibleIcon />}
                </button>
              </div>

              {errors.new_password && <small>{errors.new_password}</small>}
            </>
          ) : (
            <div className="profile-v2-value">••••••••••</div>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="profile-v2-actions">
          <div className="profile-v2-actions-copy">
            <strong>Cambios sin guardar</strong>
            <span>Revisa la información antes de continuar.</span>
          </div>

          <div className="profile-v2-actions-buttons">
            <button
              type="button"
              className="profile-v2-cancel"
              onClick={handleCancelEdition}
            >
              Cancelar
            </button>

            <button
              type="button"
              className="profile-v2-save"
              onClick={handleSubmit}
            >
              Guardar cambios
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
